import { useEnqueueSnackbar } from 'hooks/useEnqueueSnackbar';
import { useFormContext } from 'react-hook-form';
import type { MarketingProcess, PackEntry } from 'types/shared';
import type { MouseEvent } from 'react';
import { useCallback } from 'react';
import { Box } from '@mui/system';
import { Chip, Typography } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { bindDialog, usePopupState } from 'material-ui-popup-state/hooks';
import { bindTrigger } from 'material-ui-popup-state';
import TextField from './TextField';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

interface PacksWithCreativesProps {
  highlightActive?: boolean;
  allowDeleteLast?: boolean;
  onAfterDelete?: () => void;
  onAfterUndo?: (restoredPackIndex: number | null) => void;
}

export const PacksWithCreatives = ({
  highlightActive = true,
  allowDeleteLast = false,
  onAfterDelete,
  onAfterUndo,
}: PacksWithCreativesProps) => {
  const { onShowErrorAlert } = useEnqueueSnackbar();
  const { watch, setValue, trigger } = useFormContext();

  const packsWithCreatives = watch('packsWithCreatives') as PackEntry[];
  const activePacksWithCreatives = watch('activePacksWithCreatives') as number | null;

  const handleDeletePack = useCallback(
    (i: number) => {
      const clonePacksWithCreatives = [...packsWithCreatives];
      const cloneActivePacksWithCreatives = activePacksWithCreatives;
      const cloneSelectedCreatives =
        cloneActivePacksWithCreatives !== null
          ? clonePacksWithCreatives[cloneActivePacksWithCreatives]?.creatives ?? []
          : [];

      const splicePacksWithCreatives = clonePacksWithCreatives.filter(
        (_, index) => index !== i,
      );
      setValue('packsWithCreatives', splicePacksWithCreatives);

      const nextActive: number | null = splicePacksWithCreatives.length
        ? 0
        : null;
      setValue('activePacksWithCreatives', nextActive);
      setValue(
        'selectedCreatives',
        nextActive !== null
          ? splicePacksWithCreatives[nextActive]?.creatives ?? []
          : [],
        { shouldValidate: true },
      );

      onShowErrorAlert('Pack deleted', {
        delayAlert: 3500,
        onUndo: () => {
          setValue('selectedCreatives', cloneSelectedCreatives, { shouldValidate: true });
          setValue('packsWithCreatives', clonePacksWithCreatives, { shouldValidate: true });
          setValue('activePacksWithCreatives', cloneActivePacksWithCreatives, { shouldValidate: true });
          onAfterUndo?.(cloneActivePacksWithCreatives);
        },
      });

      void trigger('packsWithCreatives');
      onAfterDelete?.();
    },
    [
      onShowErrorAlert,
      onAfterDelete,
      onAfterUndo,
      activePacksWithCreatives,
      packsWithCreatives,
      setValue,
      trigger,
    ],
  );

  const handleSelectPack = useCallback(
    (i: number) => {
      const targetPack = packsWithCreatives[i];
      setValue('filterCreativeByName', '');
      setValue('activePacksWithCreatives', i);
      setValue('adType', targetPack?.adType ?? '');
      setValue('selectedCreatives', targetPack?.creatives ?? [], {
        shouldValidate: true,
      });
    },
    [setValue, packsWithCreatives],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1, rowGap: 1 }}>
      {packsWithCreatives.map(({ tag, creatives }, i) => (
        <Chip
          size={'medium'}
          key={i}
          label={
            <Tooltip
              title={
                tag.length > 38
                  ? `#${i + 1} ${tag ? tag : 'Pack'} (${creatives.length})`
                  : null
              }
              placement={'top'}
            >
              <Box
                sx={{
                  display: 'flex',
                }}
              >
                <Typography
                  component={'span'}
                  color={'inherit'}
                  sx={{
                    display: 'inline-block',
                    textOverflow: 'ellipsis',
                    maxWidth: 180,
                    overflow: 'hidden',
                  }}
                >
                  {`#${i + 1} ${tag ? tag : 'Pack'} (${creatives.length})`}
                </Typography>
              </Box>
            </Tooltip>
          }
          onClick={() => {
            handleSelectPack(i);
          }}
          onDelete={
            allowDeleteLast || packsWithCreatives.length > 1
              ? () => {
                handleDeletePack(i);
              }
              : undefined
          }
          color={
            highlightActive && String(activePacksWithCreatives) === String(i)
              ? 'primary'
              : 'default'
          }
        />
      ))}
    </Box>
  );
};

export const AddPackButton = () => {
  const { onShowAlert } = useEnqueueSnackbar();
  const { watch, setValue, trigger } = useFormContext();
  const selectedCreatives = watch('selectedCreatives') as string[];
  const marketingProcess: MarketingProcess = watch('marketingProcess');
  const packsWithCreatives = watch('packsWithCreatives') as PackEntry[];
  const activePacksWithCreatives = watch('activePacksWithCreatives') as number | null;

  const isSomePackEmpty = packsWithCreatives.some(
    (pack) => !pack.creatives.length,
  );

  const activePackCreatives =
    activePacksWithCreatives !== null
      ? (packsWithCreatives[activePacksWithCreatives]?.creatives ?? [])
      : [];

  const activePackSet = new Set(activePackCreatives);

  const isSelectionAlreadySaved =
    selectedCreatives.length > 0 &&
    selectedCreatives.length === activePackCreatives.length &&
    selectedCreatives.every(id => activePackSet.has(id));

  const hasSelection = selectedCreatives.length > 0;
  const canAddBase = !isSomePackEmpty && hasSelection;

  const isAddPackAvailable = marketingProcess === 'hypothesis_testing'
    ? canAddBase && !isSelectionAlreadySaved
    : canAddBase;

  const handleAddPack = useCallback(() => {
    const previousAdType = packsWithCreatives[packsWithCreatives.length - 1]?.adType ?? '';
    const newPacksWithCreatives: PackEntry[] = [
      ...packsWithCreatives,
      {
        tag: '',
        adType: previousAdType,
        creatives: [],
        filterCreativeByName: [],
        visual_touch_ups: true,
      },
    ];
    setValue('packsWithCreatives', newPacksWithCreatives);
    setValue('activePacksWithCreatives', newPacksWithCreatives.length - 1);
    setValue('selectedCreatives', [], { shouldValidate: true });

    onShowAlert('Pack added', {
      delayAlert: 3500,
    });

    void trigger('packsWithCreatives');
  }, [onShowAlert, setValue, packsWithCreatives, trigger]);

  return (
    <Tooltip
      title={'Select creatives to add a new pack'}
      placement={'top'}
    >
      <Box>
        <Button
          disabled={!isAddPackAvailable}
          variant="outlined"
          color="primary"
          onClick={handleAddPack}
        >
          Add pack
        </Button>
      </Box>
    </Tooltip>
  );
};

interface AddPackTagButtonProps {
  /** When set, edits this pack index instead of the active pack chip. */
  packIndex?: number;
  /** When omitted, required for Facebook hypothesis testing marketing process. */
  requireTag?: boolean;
}

export const AddPackTagButton = ({
  packIndex,
  requireTag,
}: AddPackTagButtonProps = {}) => {
  const popupState = usePopupState({
    variant: 'dialog',
    popupId:
      packIndex !== undefined ? `addPackTag-pack-${String(packIndex)}` : 'addPackTag',
  });
  const { watch, setValue, getValues, setError, clearErrors } = useFormContext();
  const packsWithCreatives = watch('packsWithCreatives') as PackEntry[];
  const marketingProcess = watch('marketingProcess') as string | undefined;
  const activePacksWithCreatives = watch('activePacksWithCreatives') as
    | number
    | null;

  const tagRequired =
    requireTag ?? marketingProcess === 'hypothesis_testing';
  const tagRequiredMessage = "Can't be empty";

  const resolvedPackIndex =
    packIndex ?? activePacksWithCreatives;

  const packTag =
    resolvedPackIndex !== null && resolvedPackIndex !== undefined
      ? (packsWithCreatives[resolvedPackIndex]?.tag ?? '')
      : '';

  const isDisabled =
    resolvedPackIndex === null ||
    resolvedPackIndex === undefined ||
    !packsWithCreatives.length ||
    !packsWithCreatives[resolvedPackIndex];

  const buttonVariant =
    packIndex !== undefined
      ? packTag
        ? 'contained'
        : 'outlined'
      : activePacksWithCreatives === null
        ? 'outlined'
        : 'contained';

  const handleCancel = useCallback(() => {
    setValue(`activePackTag`, '');
    clearErrors('activePackTag');
    popupState.close();
  }, [setValue, clearErrors, popupState]);

  const handleSave = useCallback(() => {
    if (resolvedPackIndex === null || resolvedPackIndex === undefined) return;
    const activePackTag = (getValues(`activePackTag`) as string).trim();
    if (tagRequired && !activePackTag) {
      setError('activePackTag', {
        type: 'manual',
        message: tagRequiredMessage,
      });
      return;
    }
    clearErrors('activePackTag');
    const currentPacks = getValues('packsWithCreatives') as PackEntry[];
    setValue(
      'packsWithCreatives',
      currentPacks.map((pack, index) =>
        index === resolvedPackIndex ? { ...pack, tag: activePackTag } : pack,
      ),
      { shouldValidate: true },
    );
    popupState.close();
  }, [
    setValue,
    getValues,
    setError,
    clearErrors,
    popupState,
    resolvedPackIndex,
    tagRequired,
    tagRequiredMessage,
  ]);

  const handleOpenDialog = useCallback(() => {
    if (resolvedPackIndex === null || resolvedPackIndex === undefined) return;
    clearErrors('activePackTag');
    const currentValue = getValues(
      `packsWithCreatives.${resolvedPackIndex}.tag`,
    ) as string;
    setValue('activePackTag', currentValue);
  }, [setValue, getValues, clearErrors, resolvedPackIndex]);

  return (
    <>
      <Dialog
        {...bindDialog(popupState)}
        PaperProps={{
          sx: { maxWidth: 440, minHeight: 180, width: '90%', py: 1 },
        }}
      >
        <DialogTitle>Campaign tag</DialogTitle>
        <DialogContent sx={{ px: 2, overflow: 'visible' }}>
          <TextField
            name="activePackTag"
            maxLength={64}
            required={tagRequired}
            inputLabel="Campaign tag"
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
          <Button onClick={handleCancel} variant="text" color="error">
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Button
        disabled={isDisabled}
        size="small"
        variant={buttonVariant}
        color="infoFill"
        sx={{
          '&': {
            display: 'flex',
            gap: 1 / 2,
            alignItems: 'center',
            whiteSpace: 'nowrap',
            textTransform: 'initial',
            pl: 1,
            minWidth: 20,
            maxWidth: 160,
          },
        }}
        {...bindTrigger(popupState)}
        onClick={(e: MouseEvent) => {
          bindTrigger(popupState).onClick(e);
          handleOpenDialog();
        }}
      >
        {packTag && (
          <LocalOfferIcon
            sx={(t) => ({
              fontSize: t.typography.h4.fontSize,
              color: t.palette.text.secondary,
            })}
          />
        )}
        <Typography
          variant="body2"
          component="span"
          sx={(t) => ({
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            display: 'block',
            overflow: 'hidden',
            color: isDisabled ? t.palette.text.disabled : t.palette.text.primary,
          })}
        >
          {packTag ? packTag : `+ campaign_tag`}
        </Typography>
      </Button>
    </>
  );
};
